// Presentation helpers shared across the browse screens.

export const formatCount = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

// Compact form for the sidebar counters: 412, 1.8k, 1.2M
export const compactCount = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
};

export const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('') || '·';

/**
 * Swarm health for a source row.
 *
 * A debrid-cached release plays instantly regardless of its swarm, so that
 * takes precedence. `cached` is deliberately three-valued — null means the
 * provider can't answer, which is not the same as "not cached" — so only an
 * explicit true short-circuits.
 */
export const healthOf = ({ seeders = 0, cached }) => {
  if (cached === true) return { level: 0, label: 'Instant', tone: 'ok' };
  if (seeders >= 200) return { level: 0, label: 'Fast', tone: 'ok' };
  if (seeders >= 40) return { level: 1, label: 'Healthy', tone: 'ok' };
  if (seeders >= 8) return { level: 2, label: 'Moderate', tone: 'warn' };
  if (seeders > 0) return { level: 3, label: 'Slow', tone: 'bad' };
  return { level: 3, label: 'No seeders', tone: 'bad' };
};

/**
 * How to name the debrid layer in the chrome.
 *
 * The status payload has no top-level `provider` — the name lives per account,
 * because capacity is a pool of keys rather than one login. Reading
 * `debrid.provider` gets `undefined`, which is what used to reach the screen.
 */
export const debridName = (debrid) => {
  const names = [...new Set((debrid?.accounts || []).map((a) => a.provider).filter(Boolean))];
  if (names.length === 1) return names[0];
  if (names.length > 1) return `${names.length} providers`;
  return 'debrid';
};

/**
 * The chrome's one-line summary of how playback is being served.
 *
 * "instant" is only claimed when some account can actually answer whether a hash
 * is cached. Real-Debrid and AllDebrid cannot, so saying it there promises
 * playback the app has no way to know it can deliver — and when the release then
 * has to download, the label is what made it feel broken rather than slow.
 */
export const debridLabel = (debrid) => {
  if (!debrid?.enabled) return 'peer-to-peer';
  const canTellCached = (debrid.accounts || []).some((a) => a.capabilities?.cacheCheck);
  return canTellCached ? `${debridName(debrid)} · instant` : debridName(debrid);
};

// Format callouts the indexer doesn't parse, but the release name still states.
const FORMAT_PATTERNS = [
  [/\bdolby[ .]?vision\b|\bdovi\b/i, 'DOLBY VISION'],
  [/\bhdr10\+/i, 'HDR10+'],
  [/\bhdr\b/i, 'HDR'],
  [/\batmos\b/i, 'ATMOS'],
  [/\btrue-?hd\b/i, 'TRUEHD'],
  [/\bdts-?hd\b/i, 'DTS-HD'],
  [/\bdts\b/i, 'DTS'],
  [/\b(?:e-?ac-?3|ddp)\b/i, 'EAC3'],
  [/\b7[. ]1\b/, '7.1']
];

export const formatBadges = (name = '') =>
  FORMAT_PATTERNS.filter(([pattern]) => pattern.test(name)).map(([, label]) => label);

/**
 * Badges for a source row, most informative first, capped to what the column
 * can show without wrapping. Format callouts are tagged so they can be tinted
 * apart from the plainer source/codec ones.
 */
export const badgesFor = (result) =>
  [
    ...formatBadges(result.name).map((label) => ({ label, kind: 'fmt' })),
    ...[result.quality, result.codec]
      .filter(Boolean)
      .map((value) => ({ label: value.toUpperCase(), kind: 'base' }))
  ].slice(0, 3);

export const relativeDay = (iso) => {
  if (!iso) return '';
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} weeks ago`;
  return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

export const minutesLeft = (seconds) => {
  if (!seconds || seconds < 60) return 'nearly done';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m left`;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m left`;
};
